import { Card } from "@mui/material";
import SuiBox from "components/SuiBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import React, { useCallback, useEffect, useState } from "react";
import { SuperAdminRows } from "./sub-components/rows";
import { TailSpin } from "react-loader-spinner";
import NoData from "containers/DataNotAvailBox";
import Table from "examples/Tables/Table";
import { superAdminSelector } from "store/features/superAdmin";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllProfiles } from "store/features/superAdmin";
import { fetchActiveInactiveUsers } from "store/features/superAdmin";
import Searchbar from "containers/Searchbar";
import SuperAdminSearch from "./sub-components/SuperAdminSearch";
import { userSelector } from "store/features/authSlice";
import { fetchUserProfileByUid } from "store/features/superAdmin";
import moment from "moment";
import { timestampConversion } from "utils/common";
import MyDocument from "containers/pdf/Invoice/invoice";
import { pdf } from "@react-pdf/renderer";

const SuperAdminDashboard = () => {
  const {
    profiles: clients,
    isFetching,
    activeUsers,
  } = useSelector(superAdminSelector);

  const user = useSelector(userSelector);
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [clientArray, setClientArray] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const [filterQuery, setFilterQuery] = useState({
    fromDate: null,
    toDate: null,
    status: "",
  });
  const [searchVal, setsearchVal] = useState("");

  const getLastPayment = (userInfo) => {
    return Array.isArray(userInfo?.payment) && userInfo.payment.length > 0
      ? userInfo.payment[userInfo.payment.length - 1]
      : null;
  };

  const getFormattedDate = (date) => {
    return date
      ? moment(timestampConversion(date)).local().format("MMM/DD/YYYY")
      : "N/A";
  };

  const fetchNextRenewDate = (userInfo) => {
    const sub = userInfo?.activeSubscription;
    const startDate = sub?.startDate;

    if (!sub || !startDate) return "N/A";

    const baseDate = startDate?.seconds
      ? moment(timestampConversion(startDate))
      : moment(startDate);

    return baseDate
      .add(sub?.monthOfSubscription || 0, "months")
      .add(sub?.daysLeftFromLastSubscription || 0, "days")
      .local()
      .format("MMM/DD/YYYY");
  };

  const fetchDateOfSubscription = (userInfo) => {
    const startDate = userInfo?.activeSubscription?.startDate;

    if (!startDate) return "N/A";

    let dateObj;

    if (startDate.seconds) {
      dateObj = new Date(
        startDate.seconds * 1000 + (startDate.nanoseconds || 0) / 1e6
      );
    } else {
      dateObj = new Date(startDate);
    }

    if (isNaN(dateObj.getTime())) return "N/A";

    return moment(dateObj).format("MMM/DD/YYYY");
  };

  const fetchAmountPaid = (userInfo) => {
    const lastPayment = getLastPayment(userInfo);
    return lastPayment?.totalAmount
      ? `INR ${parseFloat(lastPayment?.totalAmount)}`
      : `INR ${parseFloat(lastPayment?.amount)}`;
  };

  const fetchReferAmount = () => {
    const lastPayment = getLastPayment();
    return lastPayment?.referAmount
      ? `INR ${parseFloat(lastPayment.referAmount)}`
      : "N/A";
  };

  useEffect(() => {
    dispatch(fetchAllProfiles());
    dispatch(fetchActiveInactiveUsers());
  }, [dispatch]);

  useEffect(() => {
    const activeInactiveUsers = clients
      .filter((client) => {
        const hasValidData = client?.activeSubscription?.startDate;
        const isEmail = client.email === "fitify.superadmin@gmail.com";

        return hasValidData && !isEmail;
      })
      .map((client) => {
        const isActive = activeUsers.some((active) => active.uid === client.id);
        return {
          ...client,
          status: isActive ? "active" : "inactive",
          ...(isActive &&
            activeUsers.find((active) => active.uid === client.id)),
        };
      });

    setAllUsers(activeInactiveUsers);
  }, [clients, searchVal, activeUsers]);

  const downloadClickHandler = async (uid) => {
    try {
      const userProfile = await dispatch(fetchUserProfileByUid({ uid }));
      const userInfo = userProfile.payload.data;

      const selectedInfo = {
        nextRenewableDate: fetchNextRenewDate(userInfo),
        amountPaid: fetchAmountPaid(userInfo),
        dateOfSubscription: fetchDateOfSubscription(userInfo),
        subscriptionType: userInfo?.activeSubscription?.plan ?? "N/A",
        referAmount: fetchReferAmount(userInfo),
        gstAmount: userInfo?.payment?.at(-1)?.gstAmount ?? 0,
        subscriptionAmount:
          userInfo?.payment?.at(-1)?.subscriptionAmount ??
          userInfo?.payment?.at(-1)?.amount ??
          0,
      };

      const updatedUser = {
        ...user,
        name: userProfile.payload.data.name,
        email: userProfile.payload.data.email,
        phone: userProfile.payload.data.phone,
      };
      const blob = await pdf(
        <MyDocument user={updatedUser} subscription={selectedInfo} />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${userProfile.name || "user"}-subscription.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to generate PDF:", error);
    }
  };

  useEffect(() => {
    setRows(SuperAdminRows(clientArray, user, downloadClickHandler));
  }, [clientArray, user]);

  const columns = [
    { name: "S.no", align: "center" },
    { name: "Client Name", align: "left" },
    { name: "Subscription Start", align: "center" },
    { name: "Subscription End", align: "left" },
    { name: "Status", align: "left" },
    { name: "Plan", align: "left" },
    { name: "Subscription Amount", align: "right" },
    { name: "Phone", align: "left" },
    { name: "Email", align: "left" },
    { name: "Download Invoice", align: "left" },
  ];

  const handleSort = (columnLabel, sortKey) => {
    setSortConfig((prev) => {
      if (prev.key === sortKey) {
        return {
          key: sortKey,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key: sortKey, direction: "asc" };
    });
  };

  const filterHandler = () => {
    let filtered = allUsers.filter((item) => {
      let match = true;

      if (
        filterQuery.status?.value &&
        item.status !== filterQuery.status.value
      ) {
        match = false;
      }

      if (filterQuery.fromDate && item?.activeSubscription?.startDate) {
        const startDate = new Date(
          item.activeSubscription.startDate.seconds * 1000
        );
        const fromDate = new Date(filterQuery.fromDate);
        if (startDate < fromDate) {
          match = false;
        }
      }

      if (filterQuery.toDate && item?.activeSubscription?.startDate) {
        const startDate = new Date(
          item.activeSubscription.startDate.seconds * 1000
        );
        const toDate = new Date(filterQuery.toDate);
        if (startDate > toDate) {
          match = false;
        }
      }

      if (
        searchVal &&
        !item.name.toLowerCase().includes(searchVal.toLowerCase())
      ) {
        match = false;
      }

      return match;
    });

    setClientArray(filtered);
  };

  const resetHandler = useCallback(() => {
    setFilterQuery({
      toDate: null,
      fromDate: null,
      status: "",
    });
    setClientArray(allUsers);
  }, [allUsers]);

  useEffect(() => {
    if (!searchVal) {
      resetHandler();
      setClientArray(allUsers);
    } else {
      resetHandler();
      let filteredClients = allUsers.filter((s) => {
        let name = s?.name;
        return name.toLowerCase().includes(searchVal.toLowerCase());
      });
      setClientArray(filteredClients);
    }
  }, [isFetching, allUsers, searchVal, resetHandler]);
  return (
    <>
      <DashboardNavbar />
      <SuiBox mb={3}>
        <Card style={{ overflow: "inherit" }}>
          <SuiBox
            pt={4}
            className="search-box-div"
            style={{
              width: "100%",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <Searchbar
              placeholder={"Search Client Name"}
              searchVal={searchVal}
              searchHandler={(e) => {
                setsearchVal((_) => e);
              }}
              style_={{ marginRight: 25, marginBottom: 10 }}
              clearHandler={() => {
                setsearchVal("");
              }}
            />
          </SuiBox>
          <SuperAdminSearch
            setFilterQuery={setFilterQuery}
            filterHandler={filterHandler}
            filterQuery={filterQuery}
            resetHandler={resetHandler}
          />

          <SuiBox
            sx={{
              "& .MuiTableRow-root:not(:last-child)": {
                "& td": {
                  borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                    `${borderWidth[1]} solid ${borderColor}`,
                },
              },
            }}
          >
            {isFetching ? (
              <SuiBox
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: 30,
                }}
              >
                <TailSpin color="#17c1e8" height={60} width={60} />
              </SuiBox>
            ) : rows.length ? (
              <Table
                columns={columns}
                rows={rows}
                maxRows={10}
                handleSort={handleSort}
                sortConfig={sortConfig}
              />
            ) : (
              <NoData />
            )}
          </SuiBox>
        </Card>
      </SuiBox>
    </>
  );
};

export default SuperAdminDashboard;
